/**
 * **On the proposal** — `docs/proposal.md` §10.5.
 *
 * One visit's overrides, on the visit itself. Four questions, and **every one
 * of them starts as "same as the quote"** — a null column — so most visits
 * never open this and the quote's own settings are the only thing anybody
 * has to think about.
 *
 * That null is the whole design: it is a *value*, not an absence. An
 * estimator who sets a visit to Dates and changes their mind must be able to
 * put it back to following the quote, which is why each control has a **Same
 * as quote** segment and the request sends `null` rather than omitting the
 * field.
 *
 * The line under the timing shows **what it will actually read**, because the
 * difference between Season and Dates is the difference between a month and a
 * promise — and nobody should have to send a test to themselves to find out
 * which one they picked.
 */
import { useCallback, useEffect, useState } from 'react'
import type {
  PriceView, ProposalDetail, ProposalPhraseView, QuoteVisitView, TimingMode,
} from '@rowted/shared'
import { seasonLabel, VISIT_SCHEDULE_LABELS } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { EditorActions, EditorHeader, EditorShell } from '../builder/editor-shell.js'
import { CUSTOMER_READS_TITLE, FOLLOWING_THE_QUOTE, PROPOSAL_TIMING_LABELS } from '../builder/copy.js'
import { IconEye } from '../frame/icons.js'
import { TextInput } from '../components/form/index.js'

/** What a visit carries for this popover; null everywhere means "as the quote". */
export interface VisitProposalSettings {
  timing: TimingMode | null
  priceView: PriceView | null
  detail: ProposalDetail | null
  phraseId: string | null
  phraseText: string | null
}

export interface ProposalVisitPopoverProps {
  open: boolean
  anchor: HTMLElement | null
  visit: QuoteVisitView
  /** 1-based, for the title — the same number the customer's card shows. */
  index: number
  current: VisitProposalSettings
  onClose: () => void
  onSave: (patch: Record<string, unknown>) => Promise<void>
}

export function ProposalVisitPopover(props: ProposalVisitPopoverProps) {
  const { open, anchor, visit, index, current, onClose, onSave } = props
  const [phrases, setPhrases] = useState<ProposalPhraseView[]>([])
  /*
   * The company permission, read with the library rather than passed in.
   *
   * It arrives on the same request, which is the point: the Build tab does
   * not fetch the proposal, so a prop would have been `false` there — and a
   * company that *had* allowed dates would find the segment missing on every
   * visit, which looks exactly like the setting not working.
   */
  const [datesAllowed, setDatesAllowed] = useState(false)
  const [own, setOwn] = useState(current.phraseText ?? '')

  useEffect(() => { setOwn(current.phraseText ?? '') }, [current.phraseText])

  /*
   * The library, read once while the popover is open. It is a company-wide
   * list that nobody edits from here, so re-reading it on every keystroke
   * would be a request per character for an answer that cannot have changed.
   */
  useEffect(() => {
    if (!open) return undefined
    let live = true
    void request<{ phrases: ProposalPhraseView[]; datesAllowed: boolean }>(
      '/settings/proposal/phrases')
      .then((body) => {
        if (!live) return
        setPhrases(body.phrases)
        setDatesAllowed(body.datesAllowed)
      })
      .catch(() => { if (live) setPhrases([]) })
    return () => { live = false }
  }, [open])

  const save = useCallback((patch: Record<string, unknown>) => {
    void onSave(patch)
  }, [onSave])

  const customised = current.timing !== null || current.priceView !== null
    || current.detail !== null || current.phraseId !== null || current.phraseText !== null

  return (
    <EditorShell
      open={open}
      onClose={onClose}
      anchor={anchor}
      label={`What the customer reads for ${visit.name}`}
      /*
       * Board 7's anatomy, the same box as the visit, service and line: an
       * eye on the tile, "Visit 1 · Core treatment" under the title, and a
       * footer whose left side says whether this visit follows the quote.
       */
      header={(
        <EditorHeader
          kind="visit"
          icon={IconEye}
          name={CUSTOMER_READS_TITLE}
          facts={`Visit ${index} · ${visit.name}`}
        />
      )}
      footer={(
        <EditorActions
          fact={customised
            ? (
              /*
               * Absent, not greyed, when there is nothing to reset: a control
               * that is only ever useful after you have done something reads as
               * broken the rest of the time.
               */
              <button
                type="button"
                onClick={() => save({
                  proposalTiming: null,
                  proposalPriceView: null,
                  proposalDetail: null,
                  proposalPhraseId: null,
                  proposalPhraseText: null,
                })}
                className="text-[12.5px] font-semibold text-accent hover:text-accent-dark"
              >
                Reset to the quote’s settings
              </button>
            )
            : FOLLOWING_THE_QUOTE}
          onDone={onClose}
        />
      )}
    >
      <div className="-mx-4 -mt-3.5 flex flex-col">

        <Choice
          label="When it happens"
          value={current.timing}
          onChange={(timing) => save({ proposalTiming: timing })}
          options={[
            { value: 'count', label: PROPOSAL_TIMING_LABELS[1] },
            { value: 'frequency', label: PROPOSAL_TIMING_LABELS[2] },
            { value: 'season', label: PROPOSAL_TIMING_LABELS[3] },
            /*
             * **Dates only when the company allows it** (§10.4). Absent
             * rather than greyed: it is somebody else's decision about what
             * an estimator may promise, and a segment that looks available
             * and is not is a control people press twice.
             */
            ...(datesAllowed ? [{ value: 'dates' as const, label: PROPOSAL_TIMING_LABELS[4] }] : []),
          ]}
          note={timingNote(visit, current.timing)}
        />

        <section className="border-b border-line-faint px-4 py-3">
          <b className="text-[12.5px] font-bold text-text">The line under it</b>
          <div className="mt-2 flex max-h-[180px] flex-col overflow-y-auto">
            <PhraseRow
              name="Same as the quote"
              text="Whatever the company default says."
              picked={current.phraseId === null && !current.phraseText}
              onPick={() => save({ proposalPhraseId: null, proposalPhraseText: null })}
            />
            {phrases.map((phrase) => (
              <PhraseRow
                key={phrase.id}
                name={phrase.name}
                text={phrase.text}
                tag={phrase.isDefault ? 'company default' : undefined}
                picked={current.phraseId === phrase.id}
                /*
                 * A phrase and its own words are mutually exclusive — the
                 * database says so (`quote_visits_one_phrase`) — so picking
                 * one clears the other rather than failing the write.
                 */
                onPick={() => save({ proposalPhraseId: phrase.id, proposalPhraseText: null })}
              />
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <TextInput
              value={own}
              placeholder="Write your own for this visit"
              maxLength={160}
              onChange={(event) => setOwn(event.target.value)}
              onBlur={() => {
                if (own.trim() === (current.phraseText ?? '')) return
                save({ proposalPhraseText: own.trim() || null, proposalPhraseId: null })
              }}
            />
          </div>
        </section>

        <Choice
          label="Price shown as"
          value={current.priceView}
          onChange={(priceView) => save({ proposalPriceView: priceView })}
          options={[
            { value: 'plan', label: 'Plan' },
            { value: 'visit', label: 'Per visit' },
            { value: 'both', label: 'Both' },
          ]}
        />

        <Choice
          label="How far it opens"
          value={current.detail}
          onChange={(detail) => save({ proposalDetail: detail })}
          options={[
            { value: 'visits', label: 'Visits' },
            { value: 'services', label: 'Services' },
            { value: 'lines', label: 'Everything' },
          ]}
        />

      </div>
    </EditorShell>
  )
}

/**
 * What the visit will actually read, before anybody sends a test.
 *
 * Season resolves from the visit's own dates, so "Reads: Weekly, October to
 * early December" is the real sentence rather than an example — and a visit
 * with no dates says so, which is the case where picking Season silently does
 * nothing.
 */
function timingNote(visit: QuoteVisitView, mode: TimingMode | null): string {
  if (mode === null) return 'Same as the quote.'
  const frequency = visit.schedule === 'one_time' || visit.schedule === 'custom'
    ? null
    : VISIT_SCHEDULE_LABELS[visit.schedule]

  if (mode === 'dates') {
    return 'Dates would show the real days, so only choose it when they are held.'
  }
  if (mode === 'season') {
    const season = seasonLabel(visit.startsOn, visit.endsOn)
    if (!season) return 'This visit has no dates yet, so it will read as the frequency.'
    return `Reads: ${[frequency, season.label].filter(Boolean).join(', ')}.`
  }
  if (mode === 'frequency') {
    return frequency ? `Reads: ${frequency}.` : 'This visit has no frequency, so it reads as a count.'
  }
  return `Reads: ${visit.occurrences} ${visit.occurrences === 1 ? 'visit' : 'visits'}.`
}

function Choice<T extends string>({ label, value, onChange, options, note }: {
  label: string
  value: T | null
  onChange: (value: T | null) => void
  options: { value: T; label: string }[]
  note?: string
}) {
  return (
    <section className="border-b border-line-faint px-4 py-3">
      <b className="text-[12.5px] font-bold text-text">{label}</b>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Segment picked={value === null} onClick={() => onChange(null)}>
          {PROPOSAL_TIMING_LABELS[0]}
        </Segment>
        {options.map((option) => (
          <Segment
            key={option.value}
            picked={value === option.value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </Segment>
        ))}
      </div>
      {note && <p className="m-0 mt-1.5 text-[11.5px] leading-[1.4] text-text-muted">{note}</p>}
    </section>
  )
}

function Segment({ picked, onClick, children }: {
  picked: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={picked}
      onClick={onClick}
      className={cn('h-7 rounded-chip border px-2.5 text-[12px] font-semibold',
        picked
          ? 'border-accent-line bg-accent-soft text-accent-dark'
          : 'border-line text-text-body hover:border-line-strong')}
    >
      {children}
    </button>
  )
}

function PhraseRow({ name, text, tag, picked, onPick }: {
  name: string
  text: string
  tag?: string
  picked: boolean
  onPick: () => void
}) {
  return (
    <label className={cn('flex cursor-pointer items-start gap-2.5 rounded-field px-2 py-2',
      picked && 'bg-accent-soft')}>
      <input
        type="radio"
        checked={picked}
        onChange={onPick}
        className="mt-1 h-3.5 w-3.5 shrink-0 accent-accent"
      />
      <span className="flex min-w-0 flex-col">
        <span className="flex items-center gap-1.5">
          <b className="text-[12.5px] font-bold text-text">{name}</b>
          {tag && (
            <span className="rounded-chip bg-surface-alt px-1.5 py-px text-[10px]
                             font-semibold text-text-muted">
              {tag}
            </span>
          )}
        </span>
        <span className="text-[11.5px] leading-[1.4] text-text-muted">{text}</span>
      </span>
    </label>
  )
}
